import React, { useState, useCallback, useEffect } from 'react';

interface SearchBarProps {
  onSearch: (query: string, isRegex: boolean) => void;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  placeholder = "Search paths (supports regex)..." 
}) => {
  const [query, setQuery] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [isValidRegex, setIsValidRegex] = useState(true);

  const validateRegex = useCallback((pattern: string): boolean => {
    if (!isRegex || !pattern) return true;
    try {
      new RegExp(pattern, 'i');
      return true;
    } catch {
      return false;
    }
  }, [isRegex]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    
    const isValid = validateRegex(value);
    setIsValidRegex(isValid);
    
    if (isValid) {
      onSearch(value, isRegex);
    }
  }, [onSearch, isRegex, validateRegex]);

  const handleRegexToggle = useCallback(() => {
    const newIsRegex = !isRegex;
    setIsRegex(newIsRegex);
    
    const isValid = validateRegex(query);
    setIsValidRegex(isValid);
    
    if (isValid) {
      onSearch(query, newIsRegex);
    }
  }, [isRegex, query, onSearch, validateRegex]);

  const handleClear = useCallback(() => {
    setQuery('');
    setIsValidRegex(true);
    onSearch('', isRegex);
  }, [onSearch, isRegex]);

  useEffect(() => {
    const isValid = validateRegex(query);
    setIsValidRegex(isValid);
  }, [query, isRegex, validateRegex]);

  return (
    <div className="mb-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={`w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-2 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 transition-all duration-200 text-text-dark dark:text-dark-text ${
              !isValidRegex
                ? 'border-red-400 focus:ring-red-400 focus:border-red-400 bg-red-50 dark:bg-red-900/20'
                : 'border-border-light dark:border-dark-border focus:ring-primary-blue focus:border-primary-blue bg-bg-white dark:bg-dark-bg-secondary'
            }`}
          />
          
          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
            <svg 
              className={`w-3 sm:w-4 h-3 sm:h-4 ${!isValidRegex ? 'text-red-400' : 'text-text-gray dark:text-dark-text-secondary'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
              />
            </svg>
          </div>
          
          {query && (
            <button
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-text-gray dark:text-dark-text-secondary hover:text-primary-blue dark:hover:text-dark-primary transition-colors duration-200"
            >
              <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <button
          onClick={handleRegexToggle}
          className={`sm:ml-3 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
            isRegex
              ? 'bg-primary-blue dark:bg-dark-primary text-white shadow-light'
              : 'bg-bg-light dark:bg-dark-bg text-text-gray dark:text-dark-text-secondary hover:bg-bg-white dark:hover:bg-dark-bg-secondary hover:text-primary-blue dark:hover:text-dark-primary border border-border-light dark:border-dark-border'
          }`}
          title={isRegex ? 'Disable regex mode' : 'Enable regex mode'}
        >
          <span className="hidden sm:inline">.*</span>
          <span className="sm:hidden">RegEx</span>
        </button>
      </div>
      
      {!isValidRegex && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-xs sm:text-sm text-red-700 dark:text-red-300">
            <span className="font-medium">Invalid regex pattern:</span> Please check your regular expression syntax
          </p>
        </div>
      )}
      
      {query && isValidRegex && (
        <div className="mt-2 text-xs text-text-gray dark:text-dark-text-secondary">
          <span className="hidden sm:inline">Searching for: </span>
          <span className="font-mono font-medium break-all">{query}</span>
          {isRegex && <span className="ml-1 sm:ml-2 px-1 sm:px-2 py-1 bg-primary-blue bg-opacity-10 dark:bg-dark-primary dark:bg-opacity-20 text-primary-blue dark:text-dark-primary rounded text-xs">REGEX</span>}
        </div>
      )}
    </div>
  );
};

export default SearchBar;